<script>
import reportsService from "@/services/reports";

/**
 * @component ReportsList
 * @description Listado de los dashboards de Metabase disponibles para el
 *              tenant del usuario actual. El backend ya filtra por tenant
 *              (`listDashboards` solo devuelve los configurados para esa
 *              `Organization.key`).
 *
 *              Si la lista viene vacía, mostramos un estado "sin reportes
 *              configurados todavía" para que no quede una página rota
 *              cuando un tenant no tiene dashboards armados.
 */
const DASHBOARD_LABELS = {
  "operativo-general": {
    label: "Operativo general",
    description: "Turnos, pacientes nuevos, top médicos del último mes.",
  },
  clinico: {
    label: "Clínico",
    description:
      "Atenciones realizadas, recetas firmadas, órdenes médicas por tipo.",
  },
  comunicaciones: {
    label: "Comunicaciones",
    description: "Recordatorios enviados, próximas atenciones.",
  },
  "medico-mi-consultorio": {
    label: "Mi Consultorio",
    description:
      "Tus KPIs personales: slots publicados/tomados, atenciones, facturación, recetas, órdenes lab/imagen, top diagnósticos, modalidad de atención y más.",
  },
};

export default {
  name: "ReportsList",

  data() {
    return {
      isLoading: false,
      dashboards: [],
    };
  },

  created() {
    this.load();
  },

  methods: {
    /**
     * @description Trae la lista de dashboards disponibles para el tenant
     *              actual desde `/embedding/dashboards`.
     * @returns {Promise<void>}
     */
    async load() {
      this.isLoading = true;
      try {
        const { docs } = await reportsService.listDashboards();
        this.dashboards = docs || [];
      } catch (err) {
        this.$message.error(
          err?.response?.data?.error ||
            "No pudimos cargar la lista de reportes."
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Devuelve el label legible de un dashboard key, con
     *              fallback a la key cruda si no está en el diccionario.
     * @param {string} key
     * @returns {{label: string, description: string}}
     */
    labelOf(key) {
      return (
        DASHBOARD_LABELS[key] || {
          label: key,
          description: "",
        }
      );
    },

    /**
     * @description Navega a la vista del dashboard.
     * @param {string} key
     */
    goToDashboard(key) {
      this.$router.push({ name: "report-view", params: { key } });
    },
  },
};
</script>

<template lang="pug">
section.reports-list-page
  .page-header
    h1.page-title Reportes
    p.page-subtitle Dashboards y métricas del tenant.

  spinner(v-if="isLoading")

  .empty-state(v-else-if="!dashboards.length")
    p Todavía no hay reportes configurados para este tenant.
    small Si esperabas ver reportes acá, contactá a soporte.

  .dashboard-grid(v-else)
    .dashboard-card(
      v-for="dashboard in dashboards"
      :key="dashboard.key"
      @click="goToDashboard(dashboard.key)"
    )
      h2.dashboard-card__title {{ labelOf(dashboard.key).label }}
      p.dashboard-card__desc {{ labelOf(dashboard.key).description }}
      span.dashboard-card__cta Ver reporte →
</template>

<style lang="scss" scoped>
.reports-list-page {
  padding: 24px 32px;
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont,
    sans-serif;
}
.page-header {
  margin-bottom: 24px;
}
.page-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 800;
  color: #002d5e;
  letter-spacing: -0.01em;
}
.page-subtitle {
  margin: 0;
  font-size: 13.5px;
  color: #64748b;
}
.empty-state {
  background: #fff;
  border-radius: 12px;
  padding: 48px;
  text-align: center;
  color: #94a3b8;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}
.empty-state p {
  font-size: 15px;
  margin: 0 0 6px;
}
.empty-state small {
  font-size: 13px;
  color: #cbd5e1;
}
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.dashboard-card {
  background: #fff;
  border-radius: 12px;
  padding: 22px 24px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  border: 1.5px solid transparent;
  transition: border-color 0.12s, transform 0.12s;
}
.dashboard-card:hover {
  border-color: #0a3d7a;
  transform: translateY(-2px);
}
.dashboard-card__title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 800;
  color: #002d5e;
}
.dashboard-card__desc {
  margin: 0 0 14px;
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.45;
}
.dashboard-card__cta {
  font-size: 13px;
  font-weight: 700;
  color: #0a3d7a;
}
</style>
